/** @format */
import React, { useState, useMemo, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "./LotStat.css";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const LotStat = ({ lots, filteredLots, filter, onFilterClick }) => {
  const { t } = useTranslation();

  // état du status sélectionné - synchronisé avec le filter du parent
  const [selectedStatus, setSelectedStatus] = useState(() => {
    // Initialiser avec le filter restauré du localStorage
    if (filter?.sStatus) return filter.sStatus;
    if (filter?.filterKey) return filter.filterKey;
    return "All Lots";
  });

  // Synchroniser selectedStatus quand le filter change (ex: au refresh/restore du localStorage)
  useEffect(() => {
    if (filter?.sStatus) {
      setSelectedStatus(filter.sStatus);
    } else if (filter?.filterKey) {
      setSelectedStatus(filter.filterKey);
    } else {
      setSelectedStatus("All Lots");
    }
  }, [filter?.sStatus, filter?.filterKey]);

  const filteredData = useMemo(
    () => filteredLots || lots,
    [filteredLots, lots],
  );

  // Comptage des statuts
  const statusCountsGlobal = useMemo(
    () =>
      lots.reduce((acc, lot) => {
        const status = lot.sStatus;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [lots],
  );

  // 📊 Cards des statuts
  const globalStatusCards = useMemo(
    () =>
      Object.entries(statusCountsGlobal).map(([status, count]) => ({
        label: t(status),
        value: count,
        filterKey: status,
      })),
    [statusCountsGlobal, t],
  );

  // 📄 Stats documents filtrés (simplifiées)
  const filteredStats = useMemo(() => {
    const stats = {
      totalDocuments: 0,
      completedDocuments: 0,
      errorDocuments: 0,
      invalidDocuments: 0,
      suspectDocuments: 0,
      extractingDocuments: 0,
      reviewingDocuments: 0,
    };

    filteredData.forEach((lot) => {
      stats.totalDocuments += parseInt(lot.iCreatedDocs) || 0;
      stats.completedDocuments += parseInt(lot.iCompletedDocs) || 0;
      stats.errorDocuments += parseInt(lot.iErroredDocs) || 0;
      stats.invalidDocuments += parseInt(lot.iInvalidDocs) || 0;
      stats.suspectDocuments += parseInt(lot.iSuspectDocs) || 0;
      stats.extractingDocuments += parseInt(lot.iExtractingDocs) || 0;
      stats.reviewingDocuments += parseInt(lot.iReviewingDocs) || 0;
    });

    return stats;
  }, [filteredData]);

  // 📄 Cards documents (bleues)
  const filteredCards = useMemo(() => {
    return [
      {
        label: `${t("All Lots")} ${t("Documents")}`,
        value: filteredStats.totalDocuments,
      },
      {
        label: ` ${t("Completed")}`,
        value: filteredStats.completedDocuments,
      },
      {
        label: ` ${t("In progress")}`,
        value:
          (filteredStats.totalDocuments || 0) -
          (filteredStats.completedDocuments || 0),
      },
      {
        label: ` ${t("Extracting")}`,
        value: filteredStats.extractingDocuments,
      },
      {
        label: ` ${t("Error")}`,
        value: filteredStats.errorDocuments,
      },
      {
        label: ` ${t("Invalids")}`,
        value: filteredStats.invalidDocuments,
      },
      {
        label: ` ${t("Suspect")}`,
        value: filteredStats.suspectDocuments,
      },
      
      {
        label: `${t("Reviewing")}`,
        value: filteredStats.reviewingDocuments,
      },
    ];
  }, [filteredStats, t]);

  // 📊 Chart
  const chartData = useMemo(
    () => ({
      labels: Object.keys(statusCountsGlobal),
      datasets: [
        {
          label: t("Nombre de lot"),
          data: Object.values(statusCountsGlobal),
          backgroundColor: [
            "rgba(46,204,113,0.8)",
            "rgba(52,152,219,0.8)",
            "rgba(149,165,166,0.8)",
            "rgba(241,196,15,0.8)",
            "rgba(155,89,182,0.8)",
            "rgba(230,126,34,0.8)",
          ],
          borderColor: [
            "rgba(46,204,113,1)",
            "rgba(52,152,219,1)",
            "rgba(149,165,166,1)",
            "rgba(241,196,15,1)",
            "rgba(155,89,182,1)",
            "rgba(230,126,34,1)",
          ],
        },
      ],
    }),
    [statusCountsGlobal, t],
  );

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div className="global-stats-cadres">
  
  {/* CHART */}
  <div className="global-stats-chart">
    <Bar data={chartData} options={options} />
  </div>

  {/* DROITE */}
  <div className="global-stats-right">
    
    {/* 🔴 LOTS */}
    <p>{t("Lots")}</p>
    <div className="stats-row lots-row">
      
      <div
        className={`counter-card ${
          selectedStatus === "All Lots" ? "active" : ""
        }`}
        onClick={() => {
          setSelectedStatus("All Lots");
          onFilterClick?.("All Lots");
        }}
      >
        <p>{t("Total Lots")}</p>
        <h3>{lots.length}</h3>
      </div>

      {globalStatusCards.map((card, i) => (
        <div
          key={i}
          className={`counter-card ${
            selectedStatus === card.filterKey ? "active" : ""
          }`}
          onClick={() => {
            setSelectedStatus(card.filterKey);
            onFilterClick?.(card.filterKey);
          }}
        >
          <p>{card.label}</p>
          <h3>{card.value}</h3>
        </div>
      ))}
    </div>

    {selectedStatus && (
      <>
        <p>{t("Documents")}</p>

        <div className="stats-row docs-row">
          {filteredCards.map((card, i) => (
            <div key={i} className="filtered-counter-card">
              <p>{card.label}</p>
              <h3>{card.value}</h3>
            </div>
          ))}
        </div>
      </>
    )}

  </div>
</div>
  );
};

export default LotStat;
